import { useEffect, useRef, useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const summaryCards = [
  {
    label: '월 평균 매출액',
    value: '1,650',
    unit: '만 원',
    sub: '일 매출 75만 원 기준',
    icon: 'ri-money-dollar-circle-line',
  },
  {
    label: '월 운영 지출',
    value: '400',
    unit: '만 원',
    sub: '임대료, 소모품 등 포함',
    icon: 'ri-file-list-3-line',
  },
  {
    label: '월 예상 순이익',
    value: '1,250',
    unit: '만 원',
    sub: '순이익률 75%',
    icon: 'ri-trophy-line',
    highlight: true,
  },
];

const expenses = [
  { label: '임대료 및 관리비', amount: '1,500,000원', note: '서울/수도권 핵심 상권 10~15평 기준' },
  { label: '소모품 및 위생비', amount: '825,000원', note: '프리미엄 잉크, 일회용 니들, 멸균 키트 등 (매출의 5%)' },
  { label: '마케팅 및 홍보비', amount: '800,000원', note: '본사 협력 SNS 광고 및 포트폴리오 관리비' },
  { label: '브랜드 로열티', amount: '500,000원', note: '기술 업데이트 및 브랜드 사용권 (고정액)' },
  { label: '기타 운영비', amount: '375,000원', note: '세무 기장, 보안 시스템, 통신, 보험 등' },
];

const timeline = [
  {
    time: '11:00 ~ 14:00',
    title: '메인 세션 01',
    desc: '고단가 라인워크 작업',
    icon: 'ri-pencil-ruler-2-line',
    color: '#1A1A1A',
  },
  {
    time: '14:00 ~ 15:30',
    title: '점심 & 포트폴리오',
    desc: '여유로운 점심 및 포트폴리오 업로드 (예술적 재충전)',
    icon: 'ri-cup-line',
    color: '#888',
  },
  {
    time: '15:30 ~ 18:30',
    title: '메인 세션 02',
    desc: '집중도 높은 정밀 작업',
    icon: 'ri-focus-3-line',
    color: '#1A1A1A',
  },
  {
    time: '19:00',
    title: '퇴근 & 워라밸',
    desc: '워라밸 보장, 삶의 질 유지',
    icon: 'ri-home-heart-line',
    color: '#C9A96E',
  },
];

// Donut Chart Component
function DonutChart({ visible }: { visible: boolean }) {
  const radius = 80;
  const stroke = 22;
  const normalizedRadius = radius - stroke / 2;
  const circumference = 2 * Math.PI * normalizedRadius;

  // 75% profit (gold), 25% expense (light gray)
  const profitDash = circumference * 0.75;
  const expenseDash = circumference * 0.25;

  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    if (visible) {
      const t = setTimeout(() => setAnimated(true), 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [visible]);

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="relative" style={{ width: radius * 2, height: radius * 2 }}>
        <svg width={radius * 2} height={radius * 2} viewBox={`0 0 ${radius * 2} ${radius * 2}`}>
          {/* Background track */}
          <circle
            cx={radius}
            cy={radius}
            r={normalizedRadius}
            fill="none"
            stroke="#F0F0F0"
            strokeWidth={stroke}
          />
          {/* Expense segment (gray) - bottom */}
          <circle
            cx={radius}
            cy={radius}
            r={normalizedRadius}
            fill="none"
            stroke="#D8D8D8"
            strokeWidth={stroke}
            strokeDasharray={`${animated ? expenseDash : 0} ${circumference}`}
            strokeDashoffset={-profitDash}
            strokeLinecap="round"
            style={{
              transform: 'rotate(-90deg)',
              transformOrigin: 'center',
              transition: 'stroke-dasharray 1.2s ease-out 0.6s',
            }}
          />
          {/* Profit segment (gold) */}
          <circle
            cx={radius}
            cy={radius}
            r={normalizedRadius}
            fill="none"
            stroke="#C9A96E"
            strokeWidth={stroke}
            strokeDasharray={`${animated ? profitDash : 0} ${circumference}`}
            strokeDashoffset={0}
            strokeLinecap="round"
            style={{
              transform: 'rotate(-90deg)',
              transformOrigin: 'center',
              transition: 'stroke-dasharray 1.2s ease-out 0.2s',
            }}
          />
        </svg>
        {/* Center label */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-semibold text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>75%</span>
          <span className="text-[10px] tracking-widest text-[#999] uppercase mt-0.5">순이익률</span>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-8 space-y-3 w-full max-w-[200px]">
        <div className="flex items-center gap-3">
          <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: '#C9A96E' }} />
          <span className="text-sm text-[#1A1A1A] font-medium">순이익 75%</span>
          <span className="ml-auto text-sm text-[#1A1A1A] font-semibold">1,250만 원</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: '#D8D8D8' }} />
          <span className="text-sm text-[#999]">고정비 25%</span>
          <span className="ml-auto text-sm text-[#999]">400만 원</span>
        </div>
      </div>
    </div>
  );
}

export default function RevenueSection() {
  const { ref, visible } = useScrollAnimation(0.1);
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartVisible, setChartVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setChartVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (chartRef.current) observer.observe(chartRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-28 md:py-36 bg-[#FAFAFA] overflow-hidden" ref={ref}>
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">

        {/* ── Section Header ── */}
        <div
          className="text-center mb-20 transition-all duration-1000 ease-out"
          style={{
            opacity: 1,
            transform: 'translateY(0)',
          }}
        >
          <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Revenue Model</p>
          <h2
            className="text-3xl md:text-4xl text-[#222222] mb-5 leading-tight"
            style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}
          >
            예술적 자부심,<br />그 이상의 비즈니스 가치
          </h2>
          <p className="text-sm text-[#888] leading-relaxed max-w-xl mx-auto" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}>
            선율(SEONYUL)은 저가 경쟁을 지양합니다.<br />
            정교한 기술력과 본사의 브랜딩이 만나 실현되는 고수익 시뮬레이션을 확인하세요.
          </p>
          <div
            className="mx-auto mt-8 h-px bg-[#E5E5E5]"
            style={{ width: '80px' }}
          />
        </div>

        {/* ── Summary Cards ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-20">
          {summaryCards.map((card) => (
            <div
              key={card.label}
              className="relative p-8 border bg-white border-[#E8E8E8]"
              style={{
                opacity: 1,
                transform: 'translateY(0)',
                borderLeft: card.highlight ? '4px solid #C9A96E' : undefined,
              }}
            >
              <div className="w-10 h-10 flex items-center justify-center mb-6">
                <i className={`${card.icon} text-2xl ${card.highlight ? 'text-[#C9A96E]' : 'text-[#AAAAAA]'}`} />
              </div>
              <div className="text-xs tracking-[0.2em] uppercase mb-3 text-[#AAAAAA]" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>
                {card.label}
              </div>
              {/* 숫자 - 무조건 검정색으로! */}
              <div className="text-2xl md:text-3xl mb-2" style={{ color: card.highlight ? '#C9A96E' : '#1A1A1A' }}>
                <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
                  {card.value}
                </span>
                <span style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", fontWeight: 400, fontSize: '0.55em', letterSpacing: '0.05em', marginLeft: '0.15em' }}>
                  {card.unit}
                </span>
              </div>
              <div className="text-xs text-[#888]" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}>{card.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Expense Table + Donut Chart ── */}
        <div
          ref={chartRef}
          className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-20"
          style={{
            opacity: 1,
            transform: 'translateY(0)',
          }}
        >
          {/* Left: Expense Table */}
          <div className="bg-white border border-[#E8E8E8] p-8 md:p-10">
            <p className="text-xs tracking-[0.25em] uppercase text-[#AAAAAA] mb-8" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>상세 지출 내역</p>
            <div className="space-y-0">
              {expenses.map((exp, i) => (
                <div
                  key={exp.label}
                  className={`flex items-start justify-between py-5 ${i < expenses.length - 1 ? 'border-b border-[#F0F0F0]' : ''}`}
                >
                  <div className="flex-1 pr-4">
                    <p className="text-sm font-medium text-[#222222] mb-1" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}>{exp.label}</p>
                    <p className="text-xs text-[#AAAAAA] leading-relaxed" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}>{exp.note}</p>
                  </div>
                  <p className="text-sm whitespace-nowrap tabular-nums text-[#222222]" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}>{exp.amount}</p>
                </div>
              ))}
              {/* Total row */}
              <div className="flex items-center justify-between pt-6 mt-2 border-t-2 border-[#222222]">
                <p className="text-sm font-semibold text-[#222222] tracking-wide" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}>합계 지출액</p>
                <p className="text-xl text-[#222222]">
                  <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, letterSpacing: '-0.02em' }}>4,000,000</span>
                  <span style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", fontWeight: 400, fontSize: '0.6em', letterSpacing: '0.05em', color: '#888', marginLeft: '0.15em' }}>원</span>
                </p>
              </div>
            </div>
          </div>

          {/* Right: Donut Chart */}
          <div className="bg-white border border-[#E8E8E8] p-8 md:p-10 flex flex-col items-center justify-center">
            <p className="text-xs tracking-[0.25em] uppercase text-[#AAAAAA] mb-10 self-start" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>수익 구조 분석</p>
            <DonutChart visible={chartVisible} />
            <div className="mt-10 w-full border-t border-[#F0F0F0] pt-6">
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#AAAAAA]" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}>월 매출</span>
                <span className="text-sm text-[#222222]">
                  <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}>1,650</span>
                  <span style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", fontWeight: 400, fontSize: '0.7em', color: '#888', marginLeft: '0.1em' }}>만 원</span>
                </span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-[#AAAAAA]" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}>월 지출</span>
                <span className="text-sm text-[#888]">
                  <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}>400</span>
                  <span style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", fontWeight: 400, fontSize: '0.7em', marginLeft: '0.1em' }}>만 원</span>
                </span>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F0F0F0]">
                <span className="text-xs font-medium text-[#222222]" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}>월 순이익</span>
                <span className="text-lg text-[#C9A96E]">
                  <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}>1,250</span>
                  <span style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", fontWeight: 400, fontSize: '0.65em', marginLeft: '0.1em' }}>만 원</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Artist Daily Timeline ── */}
        <div
          className="mb-16"
          style={{
            opacity: 1,
            transform: 'translateY(0)',
          }}
        >
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.3em] uppercase text-[#AAAAAA] mb-3" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Artist Daily Schedule</p>
            <h3
              className="text-xl md:text-2xl text-[#222222]"
              style={{ fontFamily: "'Cormorant Garamond', 'Nanum Myeongjo', serif", fontWeight: 400, letterSpacing: '0.06em' }}
            >
              박리다매가 아닌, 가치 중심의 운영
            </h3>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Horizontal connector line (desktop) */}
            <div className="hidden md:block absolute top-[28px] left-[12.5%] right-[12.5%] h-px bg-[#E5E5E5] z-0" />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-4 relative z-10">
              {timeline.map((item, i) => (
                <div
                  key={item.time}
                  className="flex flex-col items-center text-center"
                  style={{
                    opacity: 1,
                    transform: 'translateY(0)',
                  }}
                >
                  {/* Icon circle */}
                  <div
                    className="w-14 h-14 flex items-center justify-center rounded-full border-2 mb-5 bg-white"
                    style={{ borderColor: item.color === '#888' ? '#E5E5E5' : item.color }}
                  >
                    <i
                      className={`${item.icon} text-xl`}
                      style={{ color: item.color }}
                    />
                  </div>
                  {/* Time badge */}
                  <span
                    className="text-[10px] tracking-widest uppercase px-3 py-1 mb-3 rounded-full"
                    style={{
                      background: item.color === '#C9A96E' ? '#C9A96E15' : '#F5F5F5',
                      color: item.color === '#C9A96E' ? '#C9A96E' : '#999',
                    }}
                  >
                    {item.time}
                  </span>
                  <p className="text-sm font-semibold text-[#1A1A1A] mb-2">{item.title}</p>
                  <p className="text-xs text-[#AAAAAA] leading-relaxed max-w-[160px]">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Disclaimer ── */}
        <div
          className="text-center"
          style={{ opacity: 1 }}
        >
          <p className="text-[11px] text-[#BBBBBB] leading-relaxed max-w-2xl mx-auto">
            ※ 상기 수익 시뮬레이션은 실제 가맹점의 평균 데이터를 바탕으로 작성되었으며,
            상권 특성 및 점주의 역량에 따라 차이가 발생할 수 있습니다.
          </p>
        </div>

      </div>
    </section>
  );
}
